// AI Readiness assessment tracks.
// Each track is a self-contained config (copy + 6 scored questions + result bands)
// consumed by the AIReadiness page. The finance track preserves the original
// /ai-readiness content exactly; the company track is the general AI-enablement
// variant used for the broader SAIA "AI-enable your whole company" landing page.

export type QuestionOption = {
  id: string;
  label: string;
  score: number;
};

export type ReadinessQuestion = {
  id: string;
  stage: string;
  title: string;
  helper: string;
  options: QuestionOption[];
};

export type ReadinessBand = {
  title: string;
  description: string;
  nextStep: string;
};

export type ReadinessTrack = {
  key: string;
  seo: { title: string; description: string; path: string };
  hero: {
    h1: string;
    subtitle: string;
    cardKicker: string;
    cardTitle: string;
    cardSubtitle: string;
  };
  introTitle: string;
  leadHeading: string;
  leadSubtitle: string;
  analyzingTitle: string;
  analyzingSubtitle: string;
  analyzingSteps: string[];
  source: string;
  offer: string;
  bookingCampaignSource: string;
  questions: ReadinessQuestion[];
  getBand: (score: number) => ReadinessBand;
};

// Public booking site that already prefills name/company/email/phone from query params.
export const BOOKING_BASE_URL = "https://booking.digital9labs.com";

export type BookingLead = {
  name?: string;
  email?: string;
  phone?: string;
  company?: string;
};

// Build the seamless hand-off URL so the visitor never re-enters their details.
export const buildBookingUrl = (track: ReadinessTrack, lead: BookingLead): string => {
  const params = new URLSearchParams();
  if (lead.name) params.set("name", lead.name);
  if (lead.company) params.set("company", lead.company);
  if (lead.email) params.set("email", lead.email);
  if (lead.phone) params.set("phone", lead.phone);
  params.set("campaign_source", track.bookingCampaignSource);
  return `${BOOKING_BASE_URL}/?${params.toString()}`;
};

// ---------------------------------------------------------------------------
// FINANCE track (original /ai-readiness content, preserved verbatim)
// ---------------------------------------------------------------------------
const financeQuestions: ReadinessQuestion[] = [
  {
    id: "monthly-close",
    stage: "Finance close",
    title: "How does your team prepare monthly finance numbers today?",
    helper: "This tells us how much repeatable structure already exists.",
    options: [
      { id: "manual", label: "Mostly manual copying, checking, and combining", score: 0 },
      { id: "spreadsheet", label: "Spreadsheets with repeated manual review", score: 1 },
      { id: "templates", label: "Structured templates with some consistency", score: 2 },
      { id: "system", label: "System-generated reports with clear owners", score: 3 },
    ],
  },
  {
    id: "source-data",
    stage: "Source data",
    title: "Where does finance source data usually come from?",
    helper: "AI extraction works best when the source pattern is known.",
    options: [
      { id: "messy", label: "Screenshots, PDFs, WhatsApp, email, and ad hoc files", score: 0 },
      { id: "mixed", label: "A mix of exports and manually maintained spreadsheets", score: 1 },
      { id: "standard", label: "Mostly standard exports from known systems", score: 2 },
      { id: "connected", label: "Connected systems or clean recurring datasets", score: 3 },
    ],
  },
  {
    id: "data-extraction",
    stage: "Extraction",
    title: "How often do you extract figures from documents or portals?",
    helper: "Recurring extraction is usually the fastest first AI workflow.",
    options: [
      { id: "never", label: "Every case is different and handled manually", score: 0 },
      { id: "often", label: "Often, but the formats are still inconsistent", score: 1 },
      { id: "repeatable", label: "Repeatable for a few common reports or documents", score: 2 },
      { id: "mapped", label: "Mapped fields and rules already exist", score: 3 },
    ],
  },
  {
    id: "approval-flow",
    stage: "Exceptions",
    title: "How visible are approvals, exceptions, and missing information?",
    helper: "AI needs clear exception handling, not just clean happy paths.",
    options: [
      { id: "hidden", label: "Mostly hidden in chats or follow-up messages", score: 0 },
      { id: "tracked", label: "Tracked manually by one or two key people", score: 1 },
      { id: "shared", label: "Shared checklist or status tracker exists", score: 2 },
      { id: "auditable", label: "Auditable statuses, owners, and timestamps exist", score: 3 },
    ],
  },
  {
    id: "report-repeatability",
    stage: "Repeatability",
    title: "If you need the same report next month, what changes?",
    helper: "The less rebuilding needed, the closer the task is to automation.",
    options: [
      { id: "rebuild", label: "Someone rebuilds most of it again", score: 0 },
      { id: "reuse", label: "Past files are reused, but still need heavy checking", score: 1 },
      { id: "steps", label: "The steps are known and mostly repeatable", score: 2 },
      { id: "one-click", label: "It is already close to a button-click workflow", score: 3 },
    ],
  },
  {
    id: "ownership",
    stage: "Ownership",
    title: "Who owns the quality of finance workflow data?",
    helper: "Clear ownership turns AI output into a dependable business process.",
    options: [
      { id: "unclear", label: "It depends on who last touched the file", score: 0 },
      { id: "senior", label: "A senior person checks and fixes issues manually", score: 1 },
      { id: "role", label: "Roles and checks are mostly defined", score: 2 },
      { id: "rules", label: "Rules, exceptions, and owners are clearly documented", score: 3 },
    ],
  },
];

const getFinanceBand = (score: number): ReadinessBand => {
  if (score <= 6) {
    return {
      title: "Manual foundation",
      description:
        "Your team can still benefit from AI, but the first win is structuring the source data, owners, and repeatable steps before automation takes over.",
      nextStep: "Start with one finance workflow and map the exact fields, documents, and checks.",
    };
  }
  if (score <= 12) {
    return {
      title: "AI-ready with cleanup",
      description:
        "You have enough structure to begin practical AI workflow work. A focused cleanup sprint can turn recurring reports or extraction tasks into repeatable runs.",
      nextStep: "Choose the highest-volume finance task and build a controlled extraction or reporting workflow.",
    };
  }
  return {
    title: "Automation-ready",
    description:
      "Your workflows already have the repeatability AI needs. You are ready to turn a documented finance process into a reusable agent-assisted workflow.",
    nextStep: "Turn the best-defined finance workflow into a one-click extraction, review, or reporting process.",
  };
};

export const financeTrack: ReadinessTrack = {
  key: "finance",
  seo: {
    title: "AI Readiness Assessment for Singapore SMEs | Digital 9 Labs",
    description:
      "Free 5-minute AI readiness check for Singapore SMEs. Identify the workflows, data, and approvals to clean up before adding AI agents.",
    path: "/ai-readiness",
  },
  hero: {
    h1: "Start your finance AI readiness check",
    subtitle:
      "Answer six quick questions, one at a time. At the end, we will analyze the pattern and show where your finance workflow stands.",
    cardKicker: "What you will learn",
    cardTitle: "Which finance workflow is ready for AI first?",
    cardSubtitle:
      "The check looks at source data, extraction, approvals, repeatability, and ownership. The goal is not a generic AI score. It is a practical next step.",
  },
  introTitle: "Ready to begin?",
  leadHeading: "Unlock your AI readiness result",
  leadSubtitle:
    "Leave your contact details so Digital9Labs can follow up with the finance workflow that looks most ready to improve.",
  analyzingTitle: "Analyzing your workflow pattern",
  analyzingSubtitle:
    "We are checking your answers across source data, repeatability, exceptions, and ownership before showing the result.",
  analyzingSteps: ["Scoring the six finance stages", "Finding the readiness band", "Preparing the practical next step"],
  source: "AI Readiness Assessment",
  offer: "Finance AI readiness assessment",
  bookingCampaignSource: "saia-finance",
  questions: financeQuestions,
  getBand: getFinanceBand,
};

// ---------------------------------------------------------------------------
// COMPANY track (general AI-enablement for any SME boss)
// ---------------------------------------------------------------------------
const companyQuestions: ReadinessQuestion[] = [
  {
    id: "manual-work",
    stage: "Manual work",
    title: "How much of your team's day is repetitive copying, chasing, and data entry?",
    helper: "The more repetitive admin there is, the more an AI assistant can take off your plate.",
    options: [
      { id: "most", label: "Most of the day - lots of manual admin and chasing", score: 0 },
      { id: "half", label: "Around half - some tools help, but plenty is manual", score: 1 },
      { id: "some", label: "Some - a few processes are already streamlined", score: 2 },
      { id: "little", label: "Very little - most routine work is already automated", score: 3 },
    ],
  },
  {
    id: "tools-connected",
    stage: "Your tools",
    title: "How connected are the tools you run the business on?",
    helper: "AI works best when it can read and act across your systems, not isolated apps.",
    options: [
      { id: "silos", label: "Separate apps and spreadsheets that do not talk to each other", score: 0 },
      { id: "partial", label: "A couple are linked, but most data is copied by hand", score: 1 },
      { id: "mostly", label: "Most core tools are integrated or exportable", score: 2 },
      { id: "unified", label: "One connected system or ERP is the source of truth", score: 3 },
    ],
  },
  {
    id: "find-info",
    stage: "Information",
    title: "When someone needs a customer history or job status, how do they find it?",
    helper: "Scattered information is the first thing an AI agent can pull together.",
    options: [
      { id: "ask", label: "Ask around or dig through chats and email", score: 0 },
      { id: "person", label: "One or two people hold most of it in their heads", score: 1 },
      { id: "shared", label: "A shared drive or tracker most people can search", score: 2 },
      { id: "single", label: "A single system with searchable, up-to-date records", score: 3 },
    ],
  },
  {
    id: "processes",
    stage: "Processes",
    title: "How documented are your core processes and SOPs?",
    helper: "Documented steps are what an AI workflow can be built to run.",
    options: [
      { id: "none", label: "Mostly in people's heads, nothing written down", score: 0 },
      { id: "some-notes", label: "A few notes exist, but they are out of date", score: 1 },
      { id: "documented", label: "Key processes are documented and mostly followed", score: 2 },
      { id: "standardised", label: "Clear, current SOPs with owners for each step", score: 3 },
    ],
  },
  {
    id: "followups",
    stage: "Follow-ups",
    title: "How are leads, tasks, and follow-ups tracked today?",
    helper: "Missed follow-ups are one of the fastest AI wins for most teams.",
    options: [
      { id: "memory", label: "Memory, sticky notes, and hoping nothing slips", score: 0 },
      { id: "spreadsheet", label: "A spreadsheet someone updates manually", score: 1 },
      { id: "tool", label: "A CRM or tool, but not everyone keeps it current", score: 2 },
      { id: "system", label: "An auditable system with reminders and owners", score: 3 },
    ],
  },
  {
    id: "access",
    stage: "Access",
    title: "If you wanted to automate a task, who can grant access to the systems and data?",
    helper: "Top-down access is what lets automation actually run end to end.",
    options: [
      { id: "unknown", label: "Not sure - no one really controls system access", score: 0 },
      { id: "external", label: "An outside vendor or IT contractor holds the keys", score: 1 },
      { id: "internal", label: "An internal admin can grant access when needed", score: 2 },
      { id: "owner", label: "As the owner, I can authorise full access quickly", score: 3 },
    ],
  },
];

const getCompanyBand = (score: number): ReadinessBand => {
  if (score <= 6) {
    return {
      title: "Manual foundation",
      description:
        "There is real upside here. The first win is getting one source of truth and documenting a couple of core processes, so an AI assistant has something dependable to run on.",
      nextStep: "Pick the single most repetitive task in your week and let us map it end to end.",
    };
  }
  if (score <= 12) {
    return {
      title: "AI-ready with cleanup",
      description:
        "You already have enough structure to start. A focused sprint can hand the most repetitive admin to an AI assistant and free your team for higher-value work.",
      nextStep: "Choose the highest-volume task across a team and build a controlled AI workflow for it.",
    };
  }
  return {
    title: "Automation-ready",
    description:
      "Your business is well organised. You are ready to put an AI assistant on real workflows and start cutting hours of routine work each week across the team.",
    nextStep: "Turn a documented process into an agent-assisted workflow your team runs every day.",
  };
};

export const companyTrack: ReadinessTrack = {
  key: "company",
  seo: {
    title: "AI Readiness Check for Your Whole Company | Digital 9 Labs",
    description:
      "Free 2-minute AI readiness check for Singapore SMEs. See how ready your business is to put AI to work across operations, and the fastest place to start.",
    path: "/ai-company",
  },
  hero: {
    h1: "How AI-ready is your company?",
    subtitle:
      "Answer six quick questions, one at a time. At the end, we will show how ready your business is for AI and the fastest place to start.",
    cardKicker: "What you will learn",
    cardTitle: "Where can AI save your team the most time first?",
    cardSubtitle:
      "The check looks at manual work, your tools, information, processes, follow-ups, and access. The goal is not a generic score. It is a practical next step.",
  },
  introTitle: "Ready to begin?",
  leadHeading: "Unlock your AI readiness result",
  leadSubtitle:
    "Leave your contact details so Digital9Labs can follow up with where AI can save your team the most time first.",
  analyzingTitle: "Analyzing how your business runs",
  analyzingSubtitle:
    "We are checking your answers across manual work, tools, information, processes, and access before showing the result.",
  analyzingSteps: ["Scoring the six business areas", "Finding the readiness band", "Preparing the practical next step"],
  source: "AI Readiness Assessment",
  offer: "Company AI readiness assessment",
  bookingCampaignSource: "saia-company",
  questions: companyQuestions,
  getBand: getCompanyBand,
};
